/**
 * Sheet editor for action_block nodes in the DAG flow builder.
 * Allows configuring action steps: notification channels, webhooks, etc.
 */
import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import {
  BUILDER_ACTION_TYPES,
  ACTION_TYPES,
  COOLDOWN_PRESETS,
  getActionLabel,
} from '@/lib/automation-trigger-types';
import type { ActionBlockConfig, ActionStep } from '@/lib/dag-types';

interface ActionEditorSheetProps {
  open: boolean;
  onClose: () => void;
  config: ActionBlockConfig;
  onSave: (config: ActionBlockConfig) => void;
  onDelete?: () => void;
}

const CHANNELS = ['in_app', 'email', 'sms', 'push'] as const;
const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-App',
  email: 'Email',
  sms: 'SMS',
  push: 'Push',
};
const RECIPIENT_OPTIONS = [
  { value: 'installer_only', label: 'Installer only' },
  { value: 'homeowner_only', label: 'Homeowner only' },
  { value: 'both', label: 'Both' },
] as const;

function generateStepId(): string {
  return `step-${Math.random().toString(36).slice(2, 10)}`;
}

function StepEditor({
  step,
  index,
  total,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  step: ActionStep;
  index: number;
  total: number;
  onChange: (step: ActionStep) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const actionConfig = step.action_config as Record<string, unknown>;
  const channels = (actionConfig.channels as string[]) ?? ['in_app', 'email'];
  const recipients = (actionConfig.recipients as string) ?? 'installer_only';

  const toggleChannel = (ch: string) => {
    const next = channels.includes(ch)
      ? channels.filter((c) => c !== ch)
      : [...channels, ch];
    onChange({
      ...step,
      action_config: { ...actionConfig, channels: next },
      subtitle: `Channel: ${next.map((c) => CHANNEL_LABELS[c] ?? c).join(' + ')}`,
    });
  };

  return (
    <div className="rounded-lg border border-border bg-wt-bg-tertiary/30 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase">
          Step {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={index === 0}
            onClick={onMoveUp}
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={index === total - 1}
            onClick={onMoveDown}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Action type */}
      <div className="space-y-1">
        <Label className="text-xs">Action Type</Label>
        <Select
          value={step.action_type}
          onValueChange={(v) =>
            onChange({
              ...step,
              action_type: v,
              title: getActionLabel(v),
              action_config: v === 'notification' ? { channels: ['in_app', 'email'], recipients: 'installer_only' } : {},
            })
          }
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BUILDER_ACTION_TYPES.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
            {ACTION_TYPES.filter((a) => a.value === 'system_control').map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label} (requires approval)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div className="space-y-1">
        <Label className="text-xs">Title</Label>
        <Input
          className="h-8 text-xs"
          value={step.title}
          onChange={(e) => onChange({ ...step, title: e.target.value })}
        />
      </div>

      {/* Notification-specific config */}
      {step.action_type === 'notification' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Channels</Label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((ch) => (
                <label key={ch} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={channels.includes(ch)}
                    onChange={() => toggleChannel(ch)}
                    className="rounded border-border"
                  />
                  {CHANNEL_LABELS[ch]}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Recipients</Label>
            <Select
              value={recipients}
              onValueChange={(v) =>
                onChange({ ...step, action_config: { ...actionConfig, recipients: v } })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECIPIENT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Webhook-specific config */}
      {step.action_type === 'webhook' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">URL</Label>
            <Input
              className="h-8 text-xs"
              placeholder="https://..."
              value={(actionConfig.url as string) ?? ''}
              onChange={(e) =>
                onChange({ ...step, action_config: { ...actionConfig, url: e.target.value } })
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Method</Label>
            <Select
              value={(actionConfig.method as string) ?? 'POST'}
              onValueChange={(v) =>
                onChange({ ...step, action_config: { ...actionConfig, method: v } })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Cooldown */}
      <div className="space-y-1">
        <Label className="text-xs">Cooldown</Label>
        <Select
          value={String(step.cooldown_minutes ?? 60)}
          onValueChange={(v) => onChange({ ...step, cooldown_minutes: Number(v) })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COOLDOWN_PRESETS.map((p) => (
              <SelectItem key={p.value} value={String(p.value)}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function ActionEditorSheet({ open, onClose, config, onSave, onDelete }: ActionEditorSheetProps) {
  const [steps, setSteps] = useState<ActionStep[]>(config.steps);
  const [title, setTitle] = useState(config.title ?? 'Actions');

  useEffect(() => {
    if (open) {
      setSteps(config.steps);
      setTitle(config.title ?? 'Actions');
    }
  }, [open, config]);

  const updateStep = (index: number, step: ActionStep) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? step : s)));
  };

  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    setSteps((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        id: generateStepId(),
        type: 'action',
        action_type: 'notification',
        action_config: { channels: ['in_app', 'email'], recipients: 'installer_only' },
        title: 'Send Notification',
        subtitle: 'Channel: In-App + Email',
      },
    ]);
  };

  const handleSave = () => {
    onSave({ ...config, title, steps });
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Actions</SheetTitle>
          <SheetDescription>Configure what happens when the trigger fires.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Block Title</Label>
            <Input
              className="h-8 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {steps.map((step, i) => (
            <StepEditor
              key={step.id}
              step={step}
              index={i}
              total={steps.length}
              onChange={(s) => updateStep(i, s)}
              onRemove={() => removeStep(i)}
              onMoveUp={() => moveStep(i, -1)}
              onMoveDown={() => moveStep(i, 1)}
            />
          ))}

          <Button variant="outline" size="sm" className="w-full" onClick={addStep}>
            <Plus className="mr-1 h-3 w-3" /> Add Step
          </Button>
        </div>

        <SheetFooter className="mt-6 flex justify-between">
          <div>
            {onDelete && (
              <Button variant="destructive" size="sm" onClick={onDelete}>
                <Trash2 className="mr-1 h-3 w-3" /> Delete Block
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Actions</Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
